import React, { useState, useEffect } from 'react';
import { Brain, Clock, Zap, TrendingUp } from 'lucide-react';
import { agentService } from '../../../services/agentService';
import { useAuth } from '../../../contexts/AuthContext';
import toast from 'react-hot-toast';

const SmartSchedulingPanel = ({ onScheduleComplete }) => {
  const { user } = useAuth();
  const [recommendations, setRecommendations] = useState([]);
  const [availableTime, setAvailableTime] = useState(60);
  const [loading, setLoading] = useState(false);
  const [autoScheduling, setAutoScheduling] = useState(false);

  useEffect(() => {
    loadRecommendations();
  }, [availableTime]);

  const loadRecommendations = async () => {
    if (!user?.id) return;

    setLoading(true);
    try {
      const { data, error } = await agentService?.getTaskRecommendations(
        user?.id,
        null,
        availableTime
      );

      if (!error && data) {
        setRecommendations(data);
      }
    } catch (error) {
      console.error('Error loading recommendations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAutoSchedule = async () => {
    if (!user?.id) return;

    setAutoScheduling(true);
    try {
      const { data, error } = await agentService?.autoScheduleTasks(
        user?.id,
        null,
        new Date()?.toISOString()?.split('T')?.[0],
        8
      );

      if (error) throw error;

      toast?.success(`Successfully scheduled ${data?.length || 0} tasks`);
      if (onScheduleComplete) onScheduleComplete();
    } catch (error) {
      console.error('Error auto-scheduling:', error);
      toast?.error('Failed to auto-schedule tasks');
    } finally {
      setAutoScheduling(false);
    }
  };

  const getPriorityColor = (score) => {
    if (score >= 80) return 'text-red-600 bg-red-100';
    if (score >= 60) return 'text-orange-600 bg-orange-100';
    if (score >= 40) return 'text-yellow-600 bg-yellow-100';
    return 'text-blue-600 bg-blue-100';
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
            <Brain className="w-5 h-5 text-purple-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Smart Scheduling</h3>
            <p className="text-sm text-gray-600">AI-powered task prioritization</p>
          </div>
        </div>

        <button
          onClick={handleAutoSchedule}
          disabled={autoScheduling}
          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {autoScheduling ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              Scheduling...
            </>
          ) : (
            <>
              <Zap className="w-4 h-4" />
              Auto-Schedule
            </>
          )}
        </button>
      </div>
      {/* Available Time Selector */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Available Time
        </label>
        <div className="flex gap-2">
          {[15, 30, 60, 120, 240]?.map((minutes) => (
            <button
              key={minutes}
              onClick={() => setAvailableTime(minutes)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                availableTime === minutes
                  ? 'bg-purple-600 text-white' :'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {minutes < 60 ? `${minutes}m` : `${minutes / 60}h`}
            </button>
          ))}
        </div>
      </div>
      {/* Recommendations */}
      <div>
        <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-gray-400" />
          Recommended Tasks
        </h4>

        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
            <p className="text-sm text-gray-600 mt-2">Loading recommendations...</p>
          </div>
        ) : recommendations?.length === 0 ? (
          <div className="text-center py-8">
            <Brain className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600">No task recommendations available</p>
            <p className="text-sm text-gray-500 mt-1">Create some tasks to get started</p>
          </div>
        ) : (
          <div className="space-y-3">
            {recommendations?.map((task) => (
              <div
                key={task?.id}
                className="p-4 border border-gray-200 rounded-lg hover:border-purple-300 transition-colors"
              >
                <div className="flex items-start justify-between mb-2">
                  <h5 className="font-medium text-gray-900">{task?.title}</h5>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(task?.priority_score)}`}>
                    {task?.priority_score}
                  </span>
                </div>
                
                {task?.description && (
                  <p className="text-sm text-gray-600 mb-2">{task?.description}</p>
                )}
                
                <div className="flex items-center gap-4 text-xs text-gray-500">
                  <div className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    <span>{task?.estimated_duration} min</span>
                  </div>
                  <span className="text-purple-600 font-medium">{task?.recommendation_reason}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {/* Info Box */}
      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-sm text-blue-900">
          <strong>Smart Scheduling</strong> uses the Eisenhower Matrix to prioritize tasks based on urgency, importance, deadlines, and estimated duration.
        </p>
      </div>
    </div>
  );
};

export default SmartSchedulingPanel;